import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import DstButton from "../../components/DstButton/DstButton";
import DstLiveTable from "../../components/DstTable/DstLiveTable";
import ListerSearch from "../../components/Lister/ListerSearch/ListerSearch";
import MultiSelectLiveDropdown from "../../components/MultiSelectLiveDropdown/MultiSelectLiveDropdown";
import Popup from "../../components/Popup/Popup";
import { useSession } from "../../contexts/SessionContext";
import { formatDateHumanly } from "../../services/TimeService";
import translate from "../../services/Translate";
import { StaffMember } from "./StaffMembers.d";
import { createStaffMember, updateStaffMember } from "./StaffMembers.function";

const StaffMembersTable = () => {
  const { language } = useSession();
  const [selectedUser, setSelectedUser] = useState<StaffMember | null>(null);
  const [editedUser, setEditedUser] = useState<any | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selectedUser) {
      setEditedUser({ ...selectedUser });
    }
  }, [selectedUser]);

  const _toggleStaffMemberRole = (key: string, value: boolean) => {
    setEditedUser((prev: any) => ({
      ...prev,
      [key]: value,
    }));
  };

  const remoteUrl = useMemo(
    () => `staff-members?refresh=${refreshKey}${searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : ""}`,
    [refreshKey, searchTerm]
  );

  const _renderBooleanIcon = (value: boolean) => {
    return value ? (
      <span className="dst-icon-check text-success s-bold" />
    ) : (
      <span className="dst-icon-x text-danger s-bold" />
    );
  };

  const columns = [
    {
      key: "id",
      name: translate(language, "PAGES.STAFF_MEMBERS.TABLE.ID"),
      sortable: true,
    },
    {
      key: "full_name",
      name: translate(language, "PAGES.STAFF_MEMBERS.TABLE.FULL_NAME"),
      sortable: true,
    },
    {
      key: "email",
      name: translate(language, "PAGES.STAFF_MEMBERS.TABLE.EMAIL"),
      sortable: true,
    },
    {
      key: "user",
      name: translate(language, "PAGES.STAFF_MEMBERS.TABLE.USER_ID"),
      sortable: true,
    },
    {
      key: "dt_assigned",
      name: translate(language, "PAGES.STAFF_MEMBERS.TABLE.DATE_ASSIGNED"),
      sortable: true,
      render: (item: any) => (item.dt_assigned ? formatDateHumanly(language, item.dt_assigned) : "—"),
    },
    {
      key: "dt_updated",
      name: translate(language, "PAGES.STAFF_MEMBERS.TABLE.UPDATED_DATE"),
      sortable: true,
      render: (item: any) => (item.dt_updated ? formatDateHumanly(language, item.dt_updated) : "—"),
    },
    {
      key: "is_active",
      name: translate(language, "PAGES.STAFF_MEMBERS.TABLE.ACTIVE"),
      sortable: true,
      render: (item: any) => _renderBooleanIcon(item.is_active),
    },
    {
      key: "user_is_active",
      name: translate(language, "PAGES.STAFF_MEMBERS.TABLE.ACTIVE_ACCOUNT_LEARN"),
      sortable: true,
      render: (item: any) => _renderBooleanIcon(item.user_is_active),
    },
    {
      key: "is_cohort_leader",
      name: translate(language, "PAGES.STAFF_MEMBERS.TABLE.COHORT_LEADER"),
      sortable: true,
      render: (item: any) => _renderBooleanIcon(item.is_cohort_leader),
    },
    {
      key: "is_meeting_animator",
      name: translate(language, "PAGES.STAFF_MEMBERS.TABLE.MEETING_ANIMATOR"),
      sortable: true,
      render: (item: any) => _renderBooleanIcon(item.is_meeting_animator),
    },
    {
      key: "is_program_manager",
      name: translate(language, "PAGES.STAFF_MEMBERS.TABLE.PROGRAM_MANAGER"),
      sortable: true,
      render: (item: any) => _renderBooleanIcon(item.is_program_manager),
    },
  ];

  const _saveStaffMember = () => {
    if (!editedUser) return;

    const onSuccess = () => {
      setShowModal(false);
      setSelectedUser(null);
      setEditedUser(null);
      setRefreshKey((prev) => prev + 1);
    };

    const action = selectedUser ? updateStaffMember : createStaffMember;
    action(language, editedUser, onSuccess, setLoading);
  };

  return (
    <div className="px-3 pt-3">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
        <h1 className="me-3">{translate(language, "PAGES.STAFF_MEMBERS.TITLE")}</h1>
        <div className="d-flex gap-2">
          <div style={{ maxWidth: 300 }}>
            <ListerSearch setSearchQuery={setSearchTerm} timeDelay={500} showSearchIcon />
          </div>
          <DstButton
            value={translate(language, "PAGES.STAFF_MEMBERS.BUTTON.ADD_MEMBER")}
            btnClass="btn btn-success"
            clickFunction={() => {
              setEditedUser({
                user: null,
                full_name: "",
                is_active: true,
                is_cohort_leader: false,
                is_meeting_animator: false,
                is_program_manager: false,
              });
              setSelectedUser(null);
              setShowModal(true);
            }}
          />
        </div>
      </div>

      <DstLiveTable
        columns={columns}
        remote={remoteUrl}
        remoteKey="staff_members"
        pagination
        paginationResizeOptions={[20, 50, 100]}
        striped
        onRowClick={(_event, row) => {
          setSelectedUser(row);
          setShowModal(true);
        }}
      />

      {editedUser && (
        <Popup
          show={showModal}
          onClose={() => {
            setShowModal(false);
            setSelectedUser(null);
            setEditedUser(null);
          }}
          modalTitle={
            selectedUser
              ? translate(language, "PAGES.STAFF_MEMBERS.MODAL.TITLE")
              : translate(language, "PAGES.STAFF_MEMBERS.MODAL.ADD_TITLE")
          }
        >
          {!selectedUser && (
            <div className="mb-3">
              <MultiSelectLiveDropdown
                category="users"
                language={language}
                isSingleSelect
                onConfirm={(items) => {
                  if (items.length > 0) {
                    const user = items[0].value;
                    setEditedUser((prev: any) => ({
                      ...prev,
                      user: user.id,
                      full_name: `${user.first_name} ${user.last_name}`,
                    }));
                  }
                }}
                label={
                  editedUser?.full_name
                    ? `${translate(language, "PAGES.STAFF_MEMBERS.MODAL.USER_SELECTED")} ${editedUser.full_name}`
                    : translate(language, "PAGES.STAFF_MEMBERS.MODAL.SELECT_USER")
                }
                toggleClass="border"
              />
            </div>
          )}

          {editedUser.full_name && (
            <p>
              <strong className="me-2">{translate(language, "PAGES.STAFF_MEMBERS.MODAL.NAME")}</strong>
              {editedUser.full_name}
              {selectedUser && (
                <Link
                  to={`/user/${editedUser.user}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-decoration-none"
                >
                  <span className="dst-icon-link-external-02 ms-2" />
                </Link>
              )}
            </p>
          )}
          <p>
            <label>
              <input
                type="checkbox"
                className="me-2"
                checked={editedUser.is_active}
                onChange={(event) => _toggleStaffMemberRole("is_active", event.target.checked)}
              />
              {translate(language, "PAGES.STAFF_MEMBERS.MODAL.ACTIVE")}
            </label>
          </p>
          <p>
            <label>
              <input
                type="checkbox"
                className="me-2"
                checked={editedUser.is_cohort_leader}
                onChange={(event) => _toggleStaffMemberRole("is_cohort_leader", event.target.checked)}
              />
              {translate(language, "PAGES.STAFF_MEMBERS.MODAL.COHORT_LEADER")}
            </label>
          </p>
          <p>
            <label>
              <input
                type="checkbox"
                className="me-2"
                checked={editedUser.is_meeting_animator}
                onChange={(event) => _toggleStaffMemberRole("is_meeting_animator", event.target.checked)}
              />
              {translate(language, "PAGES.STAFF_MEMBERS.MODAL.MEETING_ANIMATOR")}
            </label>
          </p>
          <p>
            <label>
              <input
                type="checkbox"
                className="me-2"
                checked={editedUser.is_program_manager}
                onChange={(event) => _toggleStaffMemberRole("is_program_manager", event.target.checked)}
              />
              {translate(language, "PAGES.STAFF_MEMBERS.MODAL.PROGRAM_MANAGER")}
            </label>
          </p>

          <div className="text-end mt-4">
            <DstButton
              value={translate(language, "PAGES.STAFF_MEMBERS.MODAL.SAVE")}
              btnClass="btn btn-primary"
              clickFunction={_saveStaffMember}
              loading={loading}
              mock={loading}
              btnWidth="52px"
            />
          </div>
        </Popup>
      )}
    </div>
  );
};

export default StaffMembersTable;
